import React, { useEffect, useState, useCallback, useMemo } from "react";
import Text from "components/Text";
import { RootState } from "store/ducks";
import { useSelector, useDispatch } from "react-redux";
import { BackButton } from "./components/generics/BackButton";
import { Row } from "components/Layout/Grid";
import { Button } from "components/Button";
import "./styles.scss";

import AddIcon from "img/add-icon-white.svg";
import deleteIcon from "img/icon-delete-red.svg";
import deleteIconBlack from "img/delete-black.svg";
import searchImg from "img/search-icon.svg";

import { EmptyDataMessage } from "./components/emptyDataMessage";
import SkusTable from "components/Table/Skus";
import Input from "components/Input";
import { Visibility } from "components/Visibility";
import ConfirmExclusionSKU from "./components/generics/ConfirmExclusionSKU";
import ListProductsModal from "./components/Modal/ListProducts";

import { IPriorityItem, IPriorityItemCreate } from "models/PriorityItem";
import PriorityItemsServices from "services/priority-items.service";
import { ITableModel } from "components/Table/interface";

const Skus: React.FC = () => {
  const dispatch = useDispatch();
  const priorityItems = useSelector(
    (state: RootState) => state.priorityItems
  ) as { items: IPriorityItem[] };

  const [filteredItems, setFilteredItems] = useState<IPriorityItem[]>([]);
  const [allItems, setAllItems] = useState<IPriorityItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openModalListProducts, setOpenModalListProducts] = useState(false);
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
  const [selectedIdToDelete, setSelectedIdToDelete] = useState<string | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [notFound, setNotFound] = useState(false);
  const [currentPageProduct, setCurrentPageProduct] = useState(0);
  const [codeProduct, setCodeProduct] = useState("");
  const [substanceProduct, setSubstanceProduct] = useState("");
  const [manufacturerProduct, setManufacturerProduct] = useState("");
  const [descriptionProduct, setDescriptionProduct] = useState("");

  const priorityItemsServices = useMemo(() => new PriorityItemsServices(), []);

  const sortItemsByUpdateDateDesc = (
    itemsToSort: IPriorityItem[]
  ): IPriorityItem[] => {
    return [...itemsToSort].sort((a, b) => {
      const dateA = new Date(a.updatedDate || 0).getTime();
      const dateB = new Date(b.updatedDate || 0).getTime();

      if (dateB !== dateA) {
        return dateB - dateA;
      }

      const codeA = a.supplierProductCode || "";
      const codeB = b.supplierProductCode || "";
      return codeA.localeCompare(codeB);
    });
  };

  const processFetchedData = useCallback(
    (
      fetchedData: IPriorityItem[],
      totalRecords: number,
      targetPage: number
    ) => {
      const sortedData = sortItemsByUpdateDateDesc(fetchedData);
      const calculatedTotalPages = Math.ceil(totalRecords / pageSize);

      const validCurrentPage = Math.min(
        Math.max(0, targetPage),
        Math.max(0, calculatedTotalPages - 1)
      );

      setAllItems(sortedData);
      setTotalPages(calculatedTotalPages);
      setCurrentPage(validCurrentPage);

      const startIndex = validCurrentPage * pageSize;
      const endIndex = startIndex + pageSize;
      setFilteredItems(sortedData.slice(startIndex, endIndex));

      setSelectedItems([]);
      setSelectAll(false);
    },
    [pageSize]
  );

  const fetchData = useCallback(
    async (pageToDisplay: number, currentFilter: string) => {
      setIsLoading(true);
      setSelectedItems([]);
      setSelectAll(false);
      try {
        const metaResponse = await priorityItemsServices.GetPriorityItems(
          0,
          1,
          currentFilter
        );

        const totalItemsExistentes = metaResponse.total || 0;

        if (totalItemsExistentes === 0) {
          processFetchedData([], 0, pageToDisplay);
        } else {
          const result = await priorityItemsServices.GetPriorityItems(
            0,
            totalItemsExistentes,
            currentFilter
          );

          processFetchedData(
            result.data || [],
            result.total || 0,
            pageToDisplay
          );
        }
      } catch (error: any) {
        console.error("Erro ao buscar itens prioritários:", error);

        setAllItems([]);
        setFilteredItems([]);
        setTotalPages(0);
        setCurrentPage(0);
      } finally {
        setIsLoading(false);
      }
    },
    [priorityItemsServices, processFetchedData]
  );

  useEffect(() => {
    fetchData(0, "");
  }, [fetchData]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchData(0, filter);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [filter, fetchData]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);

    const startIndex = newPage * pageSize;
    const endIndex = startIndex + pageSize;
    setFilteredItems(allItems.slice(startIndex, endIndex));

    setSelectedItems([]);
    setSelectAll(false);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value);
  };

  const handleAddPriorityItems = async (
    priorityItemsToAdd: IPriorityItemCreate[]
  ) => {
    if (priorityItemsToAdd.length === 0) return;
    setIsLoading(true);
    try {
      await priorityItemsServices.InsertPriorityItems(priorityItemsToAdd);
      await fetchData(currentPage, filter);
      setOpenModalListProducts(false);
    } catch (error) {
      console.error("Erro ao adicionar itens de prioridade:", error);
      setIsLoading(false);
    }
  };

  const handleDeleteSelectedItems = async () => {
    if (selectedItems.length === 0) return;

    const itemsToDelete = allItems.filter((item) =>
      selectedItems.includes(item.id)
    );

    if (itemsToDelete.length > 0) {
      setIsLoading(true);
      setOpenConfirmDeleteModal(false);
      try {
        await priorityItemsServices.MarkAsDeleted(
          itemsToDelete.map((item) => ({ ...item, deleted: true }))
        );

        const newTotalItems = allItems.length - itemsToDelete.length;
        const newTotalPages = Math.ceil(newTotalItems / pageSize);
        const targetPage = Math.min(
          currentPage,
          Math.max(0, newTotalPages - 1)
        );
        await fetchData(targetPage, filter);
      } catch (error) {
        console.error("Erro ao deletar itens selecionados:", error);
        setIsLoading(false);
      }
    } else {
      console.warn(
        "Itens selecionados para deletar não encontrados em allItems."
      );
      setSelectedItems([]);
      setOpenConfirmDeleteModal(false);
    }
  };

  const handleDeleteItem = async (itemToDelete: IPriorityItem) => {
    setIsLoading(true);
    setOpenModal(false);
    setSelectedIdToDelete(null);
    try {
      await priorityItemsServices.MarkAsDeleted([
        { ...itemToDelete, deleted: true },
      ]);

      const newTotalItems = allItems.length - 1;
      const newTotalPages = Math.ceil(newTotalItems / pageSize);
      const targetPage =
        filteredItems.length === 1 && currentPage > 0
          ? currentPage - 1
          : currentPage;
      const finalTargetPage = Math.min(
        targetPage,
        Math.max(0, newTotalPages - 1)
      );

      await fetchData(finalTargetPage, filter);
    } catch (error) {
      console.error("Erro ao marcar item como deletado:", error);
      setIsLoading(false);
    }
  };

  const handleOpenModalForSingleDelete = (id: string) => {
    const itemToDelete =
      allItems.find((item) => item.id === id) ||
      filteredItems.find((item) => item.id === id);
    if (itemToDelete) {
      setSelectedIdToDelete(id);
      setOpenModal(true);
    } else {
      console.warn(
        "Item para deletar (no clique do ícone) não encontrado:",
        id
      );
    }
  };

  const handleToggleSelectItem = (id: string) => {
    let newSelectedItems: string[];
    if (selectedItems.includes(id)) {
      newSelectedItems = selectedItems.filter((itemId) => itemId !== id);
    } else {
      newSelectedItems = [...selectedItems, id];
    }
    setSelectedItems(newSelectedItems);

    if (filteredItems.length > 0) {
      setSelectAll(
        newSelectedItems.length === filteredItems.length &&
          filteredItems.every((item) => newSelectedItems.includes(item.id))
      );
    } else {
      setSelectAll(false);
    }
  };

  const handleChangeSelectAllItems = () => {
    const newSelectAll = !selectAll;
    if (newSelectAll) {
      setSelectedItems(filteredItems.map((item) => item.id));
    } else {
      setSelectedItems([]);
    }
    setSelectAll(newSelectAll);
  };

  const productsModelTable: ITableModel[] = [
    {
      label: "",
      key: "selected",
      noFilter: true,
      size: 60,
      maxSize: 60,
      Component: (item: IPriorityItem) => (
        <Row className="justify-center">
          <Input
            type="checkbox"
            name={`select-${item.id}`}
            value={item.id}
            checked={selectedItems.includes(item.id)}
            onChange={() => handleToggleSelectItem(item.id)}
          />
        </Row>
      ),
    },
    {
      label: "Código",
      key: "supplierProductCode",
      size: 150,
      maxSize: 150,
      noFilter: true,
    },
    {
      label: "Descrição",
      key: "supplierProductDescription",
      size: 180,
      maxSize: 330,
      noFilter: true,
    },
    {
      label: "Substância",
      key: "molecule",
      size: 180,
      maxSize: 330,
      noFilter: true,
    },
    {
      label: "Marca",
      key: "supplier",
      size: 150,
      maxSize: 205,
      noFilter: true,
    },
    {
      label: "Categoria",
      key: "classification",
      size: 150,
      maxSize: 205,
      noFilter: true,
    },
    {
      label: "",
      key: "deleteAction",
      noFilter: true,
      size: 90,
      maxSize: 90,
      Component: (item: IPriorityItem) => (
        <img
          src={deleteIconBlack}
          alt="Excluir"
          title="Excluir este item"
          className="cursor-pointer"
          onClick={() => handleOpenModalForSingleDelete(item.id)}
          style={{
            display: "block",
            margin: "auto",
            width: "20px",
            height: "20px",
          }}
        />
      ),
    },
  ];

  const initiateDeleteSelectedItems = () => {
    if (selectedItems.length > 0) {
      setOpenConfirmDeleteModal(true);
    }
  };

  return (
    <div className="d-flex flex-column">
      <BackButton />
      <Text size={24} className="color-primary font-bold mb-4">
        SKU's prioritários
      </Text>

      <Row className="flex-1 justify-between mb-3 mt-8">
        <Button
          onClick={() => setOpenModalListProducts(true)}
          className="bg-greenDarkViveo btn-add"
          disabled={isLoading}
        >
          <img
            src={AddIcon}
            alt=""
            style={{
              width: "auto",
              height: "100%",
              maxHeight: "24px",
              marginRight: "8px",
            }}
          />
          <Text className="button-text color-white font-bold">
            Clique para adicionar SKU’s
          </Text>
        </Button>
      </Row>

      <Row className="flex-1 mt-3 mb-3">
        <Input
          placeholder="Buscar por descrição, substância ou marca"
          name="SkusSearch"
          className="w-100"
          type="text"
          icon={searchImg}
          value={filter}
          onChange={handleFilterChange}
          disabled={isLoading}
        />
      </Row>

      <Row className="items-center ml-2 mb-2">
        <Row className="items-center mr-4">
          <Input
            type="checkbox"
            id="select-all-checkbox"
            checked={selectAll}
            onChange={handleChangeSelectAllItems}
            style={{ marginRight: "8px" }}
            disabled={isLoading || filteredItems.length === 0}
            title={
              selectAll
                ? "Desmarcar todos nesta página"
                : "Selecionar todos nesta página"
            }
          />
          <label htmlFor="select-all-checkbox" style={{ cursor: "pointer" }}>
            <Text>Selecionar Todos</Text>
          </label>
        </Row>

        {selectedItems.length > 0 && (
          <Button
            onClick={initiateDeleteSelectedItems}
            className="custom-button"
            style={{
              padding: "5px 10px",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
            }}
            disabled={isLoading}
            title={`Excluir ${selectedItems.length} item(ns) selecionado(s)`}
          >
            <img
              src={deleteIcon}
              alt=""
              className="w-4 h-4 custom-icon"
              style={{ marginRight: "5px" }}
            />
            <span className="ml-2">
              Excluir selecionados ({selectedItems.length})
            </span>
          </Button>
        )}
      </Row>

      <div className="table-container mt-3">
        {isLoading ? (
          <Text className="text-center mt-5">Carregando dados...</Text>
        ) : filteredItems.length === 0 ? (
          filter ? (
            <EmptyDataMessage
              valueLineOne="Nenhum produto encontrado!"
              valueLineTwo="Tente alterar os critérios de busca."
            />
          ) : (
            <EmptyDataMessage
              valueLineOne="Nenhum SKU prioritário cadastrado."
              valueLineTwo="Clique no botão acima para adicionar."
            />
          )
        ) : (
          <SkusTable
            data={filteredItems}
            currentPage={currentPage}
            setCurrentPage={handlePageChange}
            totalPages={totalPages}
            openModalConfirmExclusion={setOpenModal}
            setSelectedIdToDelete={setSelectedIdToDelete}
            model={productsModelTable}
          />
        )}
      </div>

      <Visibility visible={openModal}>
        <ConfirmExclusionSKU
          setModalResponse={(confirm) => {
            setOpenModal(false);
            if (confirm && selectedIdToDelete) {
              const itemToDelete =
                allItems.find((item) => item.id === selectedIdToDelete) ||
                filteredItems.find((item) => item.id === selectedIdToDelete);
              if (itemToDelete) {
                handleDeleteItem(itemToDelete);
              } else {
                console.warn(
                  "Tentativa de deletar item não encontrado após confirmação:",
                  selectedIdToDelete
                );
                setSelectedIdToDelete(null);
              }
            } else {
              setSelectedIdToDelete(null);
            }
          }}
        />
      </Visibility>

      <Visibility visible={openConfirmDeleteModal}>
        <ConfirmExclusionSKU
          setModalResponse={(confirm) => {
            setOpenConfirmDeleteModal(false);
            if (confirm) {
              handleDeleteSelectedItems();
            }
          }}
        />
      </Visibility>

      <Visibility visible={openModalListProducts}>
        <ListProductsModal
          setModalResponse={() => setOpenModalListProducts(false)}
          onAddSelectedItems={handleAddPriorityItems}
          notFound={notFound}
          setNotFound={setNotFound}
          codeProduct={codeProduct}
          setCodeProduct={setCodeProduct}
          substanceProduct={substanceProduct}
          setSubstanceProduct={setSubstanceProduct}
          descriptionProduct={descriptionProduct}
          setDescriptionProduct={setDescriptionProduct}
          currentPageProduct={currentPageProduct}
          setCurrentPageProduct={setCurrentPageProduct}
          manufacturerProduct={manufacturerProduct}
          setManufacturerProduct={setManufacturerProduct}
        />
      </Visibility>
    </div>
  );
};

export default Skus;
